/**
 * THE Lyrixa AI MARK — one geometry, drawn by three renderers.
 *
 * WHAT THE MARK IS, AND WHY IT IS THIS. Four rounded strokes, centred on a horizontal
 * axis, rising fast and falling away: an AMPLITUDE ENVELOPE, the shape a sound makes when
 * it is played — a hard attack, then decay. That is the "subtle sound-wave influence" the
 * brief asks for, and it is the app's own visual language rather than a borrowed one: the
 * player already draws centred rounded strokes for a playing song (components/player/
 * Waveform, PlayingIndicator), so the logo and the product are made of the same element.
 *
 * WHAT IT DELIBERATELY IS NOT. No music note, no microphone, no robot, no brain, no AI
 * sparkle, and not two of those welded together — the brief rules each out by name, and
 * the previous mark was the first of them: lucide's `Music` glyph, a stock icon the logo
 * borrowed. It is also not an equaliser, which is the near miss worth naming: an equaliser
 * sits on a BASELINE and is read as a level meter. These strokes are centred on the
 * midline and asymmetric, which is what a waveform looks like and what a meter does not.
 *
 * WHAT IS KEPT FROM THE OLD MARK, on purpose. The amber rounded square and the near-black
 * glyph inside it. The brief says not to redesign the branding or the colour system, so the
 * badge's shape, its corner radius ratio and both colours are the ones the app already had
 * (styles/theme.c.accent, and the `#1A1300` on-accent ink used throughout). What changed is
 * what sits inside the badge.
 *
 * WHY THE GEOMETRY LIVES HERE AND NOT IN THE COMPONENT. Three surfaces need the same
 * shape in three forms that cannot share a renderer:
 *
 *   1. THE APP — components/ui/Logo.tsx, React elements.
 *   2. PRINTED AND EXPORTED DOCUMENTS — lib/printBrand.brandMarkSvg, kept as the
 *      compatibility helper name while rendering the current image asset,
 *      because a print window is written with `document.write` and has no React in it.
 *   3. THE FAVICON — public/favicon.png, copied from the current source logo asset.
 *
 * Two hand-copied path lists would drift, and a logo that is subtly different in the tab
 * from in the sidebar is worse than either version. So the numbers are here once, (1) and
 * (2) compute from them, and (3) is asserted against them as text by
 * lib/__tests__/brandMark.test.ts — which is how a stale favicon becomes a red test rather
 * than something nobody notices.
 *
 * Everything below is in the 24-unit viewBox lucide uses, so a caller that was passing a
 * pixel size to the old mark keeps working unchanged.
 */

/** The mark's coordinate space. Square, 24 units, like every icon in the app. */
export const BRAND_MARK_VIEWBOX = 24;

/**
 * Corner radius as a fraction of the badge's size.
 *
 * 0.3 — the ratio the sidebar's 26px badge already used at `borderRadius: 8`. Expressed as
 * a ratio rather than a constant so the mark is the same shape at 16px and at 96px; a fixed
 * radius makes a large badge look like a rounded rectangle and a small one like a circle.
 */
export const BRAND_MARK_RADIUS_RATIO = 0.3;

/** Stroke width, in viewBox units. */
const STROKE_WIDTH = 2.4;

/** Gap between strokes. Narrower than the strokes, so they read as one mark. */
const STROKE_GAP = 1.2;

/**
 * The envelope, as stroke heights from left to right.
 *
 * ATTACK THEN DECAY, and the asymmetry is the whole design: a short onset, an immediate
 * peak, then two strokes falling away. Symmetric heights would read as an equaliser at
 * rest and would have no direction; a rising ramp would read as a bar chart or a signal-
 * strength meter. This reads as something being played.
 *
 * The peak is 16 rather than the full 24 so the tallest stroke keeps 4 units of air inside
 * the badge — at favicon size, a stroke that touches the rounded corner reads as a
 * rendering fault.
 */
const STROKE_HEIGHTS: readonly number[] = [6, 16, 11.5, 7.5];

/** One stroke of the mark, as the rounded rect that draws it. */
export interface BrandMarkStroke {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Half the width, so the caps are fully round rather than merely soft. */
  rx: number;
}

/**
 * The four strokes, positioned and centred.
 *
 * Centred on BOTH axes and computed rather than typed out: the horizontal inset falls out
 * of the widths and gaps, so changing a stroke width cannot leave the mark sitting slightly
 * left of centre — which is the kind of error that is invisible in a sidebar and obvious in
 * a 16px favicon.
 */
export const BRAND_MARK_STROKES: readonly BrandMarkStroke[] = (() => {
  const count = STROKE_HEIGHTS.length;
  const span = count * STROKE_WIDTH + (count - 1) * STROKE_GAP;
  const left = (BRAND_MARK_VIEWBOX - span) / 2;
  const middle = BRAND_MARK_VIEWBOX / 2;

  return STROKE_HEIGHTS.map((height, index) => ({
    x: left + index * (STROKE_WIDTH + STROKE_GAP),
    y: middle - height / 2,
    width: STROKE_WIDTH,
    height,
    rx: STROKE_WIDTH / 2,
  }));
})();

/**
 * The name every rendering of the mark is announced by.
 *
 * One constant because it appears in three places — the React component's `<title>`, the
 * print SVG's `aria-label`, and the favicon — and a logo that is called something slightly
 * different by a screen reader on each surface is the same drift problem as the geometry.
 */
export const BRAND_NAME = 'Lyrixsa AI';

/** The brand amber. Mirrors styles/theme.c.accent, which is the source of truth. */
export const BRAND_AMBER = '#F2A93C';

/**
 * The near-black used on top of amber, here and everywhere else in the app.
 *
 * Not `text1`: light text on amber measures 1.9:1. This is the ink the accent has always
 * been paired with — see the accent buttons, the credit badge, and DISABLED_OPACITY's note
 * in styles/theme.ts.
 */
export const BRAND_INK = '#1A1300';

/**
 * Rounds a coordinate for output, so the emitted SVG is readable and comparable.
 *
 * Three decimals is far finer than a pixel at any size the mark is drawn, and fixing the
 * precision is what lets the favicon be compared to the computed geometry as TEXT — see
 * this module's header, and the test that does it.
 */
export function brandCoord(value: number): string {
  return String(Number(value.toFixed(3)));
}
